import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteWorkflowSchedule } from '@/hooks'
import { extractValidationMessage } from '@/lib/validationErrors'
import { getErrorMessage } from '@/api/errors'
import { Trash2, X } from 'lucide-react'
import { toast } from 'sonner'

interface Props {
  projectId: string
  schedule: { id: string; name: string } | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onDeleted?: () => void
}

export function DeleteScheduleDialog({ projectId, schedule, open, onOpenChange, onDeleted }: Props) {
  const deleteSchedule = useDeleteWorkflowSchedule(projectId)

  if (!schedule) return null

  const handleDelete = async () => {
    try {
      await deleteSchedule.mutateAsync(schedule.id)
      toast.success(`Schedule "${schedule.name}" deleted`)
      onOpenChange(false)
      onDeleted?.()
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to delete schedule.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Delete schedule</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none" aria-label="Close">
            <X className="h-6 w-6" />
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm leading-[1.6] tracking-[0.015em] text-foreground">
          Delete the schedule "<span className="font-bold">{schedule.name}</span>"? It stops firing immediately.
          The schedule and its history are removed from view; runs it already started are not affected.
        </DialogDescription>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-10 rounded-lg px-4 font-bold">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteSchedule.isPending}
            className="h-10 rounded-lg px-4 gap-2 font-bold"
            data-testid="delete-schedule-confirm-button"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {deleteSchedule.isPending ? 'Deleting...' : 'Delete schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
